import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type TextScale = "default" | "large" | "xlarge";
export type InterfaceDensity = "comfortable" | "compact";
export type ContrastMode = "standard" | "high";
export type MotionMode = "system" | "reduced";
export type GraphEffects = "full" | "calm";
export type ReadingWidth = "comfortable" | "wide";

export interface QolPreferences {
  textScale: TextScale;
  density: InterfaceDensity;
  contrast: ContrastMode;
  motion: MotionMode;
  graphEffects: GraphEffects;
  readingWidth: ReadingWidth;
  sidebarCollapsed: boolean;
  autoRefresh: boolean;
  showHints: boolean;
}

export const DEFAULT_QOL_PREFERENCES: QolPreferences = {
  textScale: "default",
  density: "comfortable",
  contrast: "standard",
  motion: "system",
  graphEffects: "full",
  readingWidth: "comfortable",
  sidebarCollapsed: false,
  autoRefresh: true,
  showHints: true,
};

const CHOICES = {
  textScale: new Set<TextScale>(["default", "large", "xlarge"]),
  density: new Set<InterfaceDensity>(["comfortable", "compact"]),
  contrast: new Set<ContrastMode>(["standard", "high"]),
  motion: new Set<MotionMode>(["system", "reduced"]),
  graphEffects: new Set<GraphEffects>(["full", "calm"]),
  readingWidth: new Set<ReadingWidth>(["comfortable", "wide"]),
};

export function parseQolPreferences(value: unknown): QolPreferences {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("QoL preferences must be a JSON object.");
  }
  const candidate = value as Partial<QolPreferences>;
  return {
    textScale: CHOICES.textScale.has(candidate.textScale as TextScale) ? candidate.textScale as TextScale : DEFAULT_QOL_PREFERENCES.textScale,
    density: CHOICES.density.has(candidate.density as InterfaceDensity) ? candidate.density as InterfaceDensity : DEFAULT_QOL_PREFERENCES.density,
    contrast: CHOICES.contrast.has(candidate.contrast as ContrastMode) ? candidate.contrast as ContrastMode : DEFAULT_QOL_PREFERENCES.contrast,
    motion: CHOICES.motion.has(candidate.motion as MotionMode) ? candidate.motion as MotionMode : DEFAULT_QOL_PREFERENCES.motion,
    graphEffects: CHOICES.graphEffects.has(candidate.graphEffects as GraphEffects) ? candidate.graphEffects as GraphEffects : DEFAULT_QOL_PREFERENCES.graphEffects,
    readingWidth: CHOICES.readingWidth.has(candidate.readingWidth as ReadingWidth) ? candidate.readingWidth as ReadingWidth : DEFAULT_QOL_PREFERENCES.readingWidth,
    sidebarCollapsed: typeof candidate.sidebarCollapsed === "boolean" ? candidate.sidebarCollapsed : DEFAULT_QOL_PREFERENCES.sidebarCollapsed,
    autoRefresh: typeof candidate.autoRefresh === "boolean" ? candidate.autoRefresh : DEFAULT_QOL_PREFERENCES.autoRefresh,
    showHints: typeof candidate.showHints === "boolean" ? candidate.showHints : DEFAULT_QOL_PREFERENCES.showHints,
  };
}

function safeScope(scope: string) {
  return scope.replace(/[^A-Za-z0-9._:-]/g, "_").slice(0, 180) || "local";
}

export function qolStorageKey(scope: string) {
  return `lattice:qol:v1:${safeScope(scope)}`;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

export function useQolPreferences(scope: string) {
  const storageKey = useMemo(() => qolStorageKey(scope), [scope]);
  const [preferences, setPreferencesState] = useState<QolPreferences>(() => {
    try {
      return parseQolPreferences(readJson(storageKey, DEFAULT_QOL_PREFERENCES));
    } catch {
      return DEFAULT_QOL_PREFERENCES;
    }
  });

  useEffect(() => {
    try {
      setPreferencesState(parseQolPreferences(readJson(storageKey, DEFAULT_QOL_PREFERENCES)));
    } catch {
      setPreferencesState(DEFAULT_QOL_PREFERENCES);
    }
  }, [storageKey]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.textScale = preferences.textScale;
    root.dataset.density = preferences.density;
    root.dataset.contrast = preferences.contrast;
    root.dataset.motion = preferences.motion;
    root.dataset.graphEffects = preferences.graphEffects;
    root.dataset.readingWidth = preferences.readingWidth;
    root.dataset.showHints = String(preferences.showHints);
    root.style.colorScheme = "dark";
  }, [preferences]);

  useEffect(() => {
    function sync(event: StorageEvent) {
      if (event.key !== storageKey || !event.newValue) return;
      try {
        setPreferencesState(parseQolPreferences(JSON.parse(event.newValue)));
      } catch {
        // A bad value in another tab must not break this workspace.
      }
    }
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [storageKey]);

  const setPreferences = useCallback((next: QolPreferences | ((current: QolPreferences) => QolPreferences)) => {
    setPreferencesState((current) => {
      const resolved = parseQolPreferences(typeof next === "function" ? next(current) : next);
      window.localStorage.setItem(storageKey, JSON.stringify(resolved));
      return resolved;
    });
  }, [storageKey]);

  const patchPreferences = useCallback((patch: Partial<QolPreferences>) => {
    setPreferences((current) => ({ ...current, ...patch }));
  }, [setPreferences]);

  const resetPreferences = useCallback(() => setPreferences(DEFAULT_QOL_PREFERENCES), [setPreferences]);

  return { preferences, setPreferences, patchPreferences, resetPreferences, storageKey };
}

export interface RecentNote {
  id: string;
  title: string;
  type: string;
  openedAt: string;
}

export interface QolHistory {
  recentPages: string[];
  recentNotes: RecentNote[];
  recentSearches: string[];
  favoritePages: string[];
}

const EMPTY_HISTORY: QolHistory = { recentPages: [], recentNotes: [], recentSearches: [], favoritePages: [] };

export function historyStorageKey(scope: string) {
  return `lattice:qol-history:v1:${safeScope(scope)}`;
}

function normalizeHistory(value: unknown): QolHistory {
  const candidate = value && typeof value === "object" && !Array.isArray(value) ? value as Partial<QolHistory> : {};
  const strings = (input: unknown, limit: number) => Array.isArray(input)
    ? [...new Set(input.filter((item): item is string => typeof item === "string" && item.trim().length > 0).map((item) => item.trim()))].slice(0, limit)
    : [];
  const recentNotes = Array.isArray(candidate.recentNotes)
    ? candidate.recentNotes.filter((note): note is RecentNote => Boolean(note && typeof note.id === "string" && typeof note.title === "string" && typeof note.type === "string" && typeof note.openedAt === "string")).slice(0, 12)
    : [];
  return {
    recentPages: strings(candidate.recentPages, 8),
    recentNotes,
    recentSearches: strings(candidate.recentSearches, 8),
    favoritePages: strings(candidate.favoritePages, 8),
  };
}

export function useQolHistory(scope: string) {
  const storageKey = useMemo(() => historyStorageKey(scope), [scope]);
  const [history, setHistoryState] = useState<QolHistory>(() => normalizeHistory(readJson(storageKey, EMPTY_HISTORY)));

  useEffect(() => setHistoryState(normalizeHistory(readJson(storageKey, EMPTY_HISTORY))), [storageKey]);

  const update = useCallback((recipe: (current: QolHistory) => QolHistory) => {
    setHistoryState((current) => {
      const next = normalizeHistory(recipe(current));
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [storageKey]);

  const recordPage = useCallback((page: string) => update((current) => ({
    ...current,
    recentPages: [page, ...current.recentPages.filter((value) => value !== page)],
  })), [update]);

  const recordNote = useCallback((note: Omit<RecentNote, "openedAt">) => update((current) => ({
    ...current,
    recentNotes: [{ ...note, openedAt: new Date().toISOString() }, ...current.recentNotes.filter((value) => value.id !== note.id)],
  })), [update]);

  const recordSearch = useCallback((query: string) => {
    const normalized = query.trim();
    if (normalized.length < 2) return;
    update((current) => ({ ...current, recentSearches: [normalized, ...current.recentSearches.filter((value) => value.toLocaleLowerCase() !== normalized.toLocaleLowerCase())] }));
  }, [update]);

  const toggleFavoritePage = useCallback((page: string) => update((current) => ({
    ...current,
    favoritePages: current.favoritePages.includes(page)
      ? current.favoritePages.filter((value) => value !== page)
      : [page, ...current.favoritePages],
  })), [update]);

  return { history, recordPage, recordNote, recordSearch, toggleFavoritePage };
}

export function useStoredView<T>(scope: string, name: string, fallback: T) {
  const key = useMemo(() => `lattice:view:v1:${safeScope(scope)}:${safeScope(name)}`, [name, scope]);
  const fallbackRef = useRef(fallback);
  const [value, setValueState] = useState<T>(() => readJson(key, fallback));
  useEffect(() => setValueState(readJson(key, fallbackRef.current)), [key]);
  const setValue = useCallback((next: T | ((current: T) => T)) => {
    setValueState((current) => {
      const resolved = typeof next === "function" ? (next as (current: T) => T)(current) : next;
      window.localStorage.setItem(key, JSON.stringify(resolved));
      return resolved;
    });
  }, [key]);
  return [value, setValue] as const;
}

export interface QolCatalogItem {
  id: number;
  area: "Shell" | "Accessibility" | "Command" | "Library" | "Editor" | "Graph";
  title: string;
  evidence: string;
}

const TITLES: Array<[QolCatalogItem["area"], string, string]> = [
  ["Shell", "Skip directly to the main workspace", "App skip link"],
  ["Shell", "Move focus to the new page after navigation", "App route focus"],
  ["Shell", "Keep the browser title in sync with the page", "App document title"],
  ["Shell", "Announce route changes to screen readers", "App route live region"],
  ["Shell", "Start new pages at the top", "App navigation scroll"],
  ["Shell", "Restore remembered page scroll on history navigation", "App scroll map"],
  ["Shell", "Show a persistent offline banner", "App connectivity banner"],
  ["Shell", "Retry connectivity from the banner", "App connectivity retry"],
  ["Shell", "Refresh the current workspace on demand", "Topbar refresh"],
  ["Shell", "Show when the workspace was last refreshed", "Topbar refresh status"],
  ["Shell", "Collapse the desktop sidebar", "Sidebar collapse control"],
  ["Shell", "Remember the sidebar state", "Scoped QoL preferences"],
  ["Shell", "Provide thumb-friendly mobile navigation", "Mobile quick nav"],
  ["Shell", "Keep text labels on mobile navigation", "Mobile quick nav labels"],
  ["Shell", "Navigate back from the top bar", "Topbar back action"],
  ["Shell", "Favorite the current workspace page", "Topbar favorite action"],
  ["Shell", "Show favorite pages as a navigation group", "Sidebar favorites"],
  ["Shell", "Remember recent pages", "Scoped QoL history"],
  ["Shell", "Remember recently opened notes", "Scoped QoL history"],
  ["Shell", "Open help with the question-mark key", "Global shortcuts"],
  ["Accessibility", "Offer a larger text scale", "Interface preferences"],
  ["Accessibility", "Offer an extra-large text scale", "Interface preferences"],
  ["Accessibility", "Offer a compact information density", "Interface preferences"],
  ["Accessibility", "Retain a comfortable density", "Interface preferences"],
  ["Accessibility", "Offer a high-contrast palette", "Interface preferences"],
  ["Accessibility", "Honor the standard palette", "Interface preferences"],
  ["Accessibility", "Follow the system motion preference", "Interface preferences"],
  ["Accessibility", "Force reduced motion when requested", "Interface preferences"],
  ["Accessibility", "Calm graph effects without hiding the graph", "Interface preferences"],
  ["Accessibility", "Keep full graph effects as an option", "Interface preferences"],
  ["Accessibility", "Constrain reading width for long text", "Interface preferences"],
  ["Accessibility", "Allow a wide reading canvas", "Interface preferences"],
  ["Accessibility", "Scope preferences to workspace and user", "Scoped QoL preferences"],
  ["Accessibility", "Synchronize preferences across tabs", "Storage event sync"],
  ["Accessibility", "Reset interface preferences safely", "Interface preferences"],
  ["Accessibility", "Export interface preferences as JSON", "Interface preferences"],
  ["Accessibility", "Import validated preference JSON", "Interface preferences"],
  ["Accessibility", "Reject malformed preference imports", "Preference parser"],
  ["Accessibility", "Keep touch targets at least 44 pixels", "QoL responsive CSS"],
  ["Accessibility", "Suppress hover-only motion for coarse pointers", "QoL responsive CSS"],
  ["Command", "Move down command results with ArrowDown", "Command keyboard model"],
  ["Command", "Move up command results with ArrowUp", "Command keyboard model"],
  ["Command", "Jump to the first command with Home", "Command keyboard model"],
  ["Command", "Jump to the last command with End", "Command keyboard model"],
  ["Command", "Run the highlighted command with Enter", "Command keyboard model"],
  ["Command", "Expose the highlighted command to assistive tech", "Command active descendant"],
  ["Command", "Clear the command query in one click", "Command clear action"],
  ["Command", "Show the ranked result count", "Command search status"],
  ["Command", "Surface recently opened notes", "Command recent notes"],
  ["Command", "Surface recently visited pages", "Command recent pages"],
  ["Command", "Surface favorite pages first", "Command favorites"],
  ["Command", "Filter page commands by the typed query", "Command filtering"],
  ["Command", "Filter action commands by the typed query", "Command filtering"],
  ["Command", "Highlight the matching title fragment", "Command match marks"],
  ["Command", "Offer note creation when search is empty", "Command empty action"],
  ["Command", "Show keyboard selection visually", "Command active state"],
  ["Command", "Scroll the selected command into view", "Command keyboard model"],
  ["Command", "Remember recent knowledge searches", "Command search history"],
  ["Command", "Re-run a recent search with one click", "Command search history"],
  ["Command", "Open the command center with Ctrl/Cmd+Shift+P", "Global shortcuts"],
  ["Library", "Remember the selected collection tab", "Library scoped view"],
  ["Library", "Remember card or list layout", "Library scoped view"],
  ["Library", "Remember the selected sort order", "Library scoped view"],
  ["Library", "Sort by newest update", "Library sorting"],
  ["Library", "Sort by oldest update", "Library sorting"],
  ["Library", "Sort titles A to Z", "Library sorting"],
  ["Library", "Sort titles Z to A", "Library sorting"],
  ["Library", "Clear the text query in one click", "Library clear search"],
  ["Library", "Clear all active filters in one click", "Library clear lens"],
  ["Library", "Show an exact visible-result count", "Library result status"],
  ["Library", "Highlight title matches safely", "Library match marks"],
  ["Library", "Search taxonomy names", "Library taxonomy search"],
  ["Library", "Expand and collapse the taxonomy list", "Library taxonomy expansion"],
  ["Library", "Distinguish tags from taxonomy in active filters", "Library classification label"],
  ["Library", "Explain how to recover from an empty lens", "Library empty-state actions"],
  ["Editor", "Show a live word count", "Editor writing statistics"],
  ["Editor", "Show a live character count", "Editor writing statistics"],
  ["Editor", "Estimate reading time", "Editor writing statistics"],
  ["Editor", "Copy a note wikilink", "Editor copy actions"],
  ["Editor", "Copy raw Markdown", "Editor copy actions"],
  ["Editor", "Toggle a distraction-free full-screen editor", "Editor focus mode"],
  ["Editor", "Save with Ctrl+S", "Editor keyboard actions"],
  ["Editor", "Save with Cmd+S", "Editor keyboard actions"],
  ["Editor", "Indent Markdown with Tab", "Editor keyboard actions"],
  ["Editor", "Unindent Markdown with Shift+Tab", "Editor keyboard actions"],
  ["Editor", "Deduplicate tags case-insensitively", "Editor metadata normalization"],
  ["Editor", "Deduplicate subcategories case-insensitively", "Editor metadata normalization"],
  ["Editor", "Expose autosave-pending state", "Editor save status"],
  ["Editor", "Show title length before saving", "Editor writing statistics"],
  ["Editor", "Hide keyboard hints when the user prefers", "Interface hint preference"],
  ["Graph", "Remember graph filters per user and workspace", "Graph scoped view"],
  ["Graph", "Clear every graph filter in one click", "Graph clear lens"],
  ["Graph", "Clear graph search in one click", "Graph clear search"],
  ["Graph", "Open the first graph match with Enter", "Graph search keyboard action"],
  ["Graph", "Copy a selected node as a wikilink", "Graph copy actions"],
  ["Graph", "Copy a selected node title", "Graph copy actions"],
  ["Graph", "Clear the context tray in one click", "Graph tray actions"],
  ["Graph", "Announce the tray selection count", "Graph tray live region"],
  ["Graph", "Remember constellation visibility", "Graph scoped view"],
  ["Graph", "Apply the calm-effects preference to graph physics", "KnowledgeGraph preferences"],
];

export const QOL_CATALOG: QolCatalogItem[] = TITLES.map(([area, title, evidence], index) => ({ id: index + 1, area, title, evidence }));
